import {
  CHOICE_BOOKING_QUESTION_TYPES,
  MAX_BOOKING_QUESTION_LABEL_CHARS,
  MAX_BOOKING_QUESTION_OPTION_CHARS,
  MAX_BOOKING_QUESTION_OPTIONS,
  MAX_BOOKING_QUESTIONS,
  type AppointmentTypeDto,
  type BookingQuestion,
  type BookingQuestionInput,
  type BookingQuestionType,
} from '@shared/core/appointment-types'
import { bookingOptionDuplicateKey, countCodePoints, sanitizeBookingText } from '@shared/core/bookings'

export type EditorQuestion = BookingQuestionInput & { key: string }

export interface QuestionsEditorState {
  questions: EditorQuestion[]
  baseline: EditorQuestion[]
  /** Local key of the Message row the editor seeded in create mode; never matched by `origin`. */
  seededMessageKey: string | null
}

export type QuestionTileId = 'short_text' | 'long_text' | 'dropdown' | 'multiple_choice' | 'yes_no' | 'checkboxes'

export interface QuestionTile {
  id: QuestionTileId
  type: BookingQuestionType
  label: string
  hint: string
  presetOptions?: string[]
}

export interface DraftOption {
  key: string
  value: string
}

export interface QuestionDraft {
  type: BookingQuestionType
  label: string
  required: boolean
  options: DraftOption[]
}

export interface DraftErrors {
  label?: string
  options?: string
  optionErrors: Record<string, string>
}

export interface InsertOptionResult {
  draft: QuestionDraft
  insertedKey: string | null
}

export interface CopyResult {
  state: QuestionsEditorState
  message: string | null
}

export const SEEDED_MESSAGE_LABEL = 'Message'
export const QUESTIONS_LIMIT_NOTE = `You can add up to ${MAX_BOOKING_QUESTIONS} questions.`
export const OPTIONS_LIMIT_NOTE = `You can add up to ${MAX_BOOKING_QUESTION_OPTIONS} options.`
export const QUESTIONS_CONFLICT_MESSAGE = 'This appointment type was changed somewhere else. Reload to see the latest.'
export const FINISH_OPEN_QUESTION_MESSAGE = 'Add or cancel the question you are editing before saving.'
export const LABEL_REQUIRED_ERROR = 'Enter a question.'
export const LABEL_TOO_LONG_ERROR = `Maximum ${MAX_BOOKING_QUESTION_LABEL_CHARS} characters.`
export const OPTION_EMPTY_ERROR = 'Enter an option.'
export const OPTION_TOO_LONG_ERROR = `Maximum ${MAX_BOOKING_QUESTION_OPTION_CHARS} characters.`
export const OPTION_DUPLICATE_ERROR = 'This option is already in the list.'
export const OPTIONS_REQUIRED_ERROR = 'Add at least one option.'

export const QUESTION_TILES: QuestionTile[] = [
  { id: 'short_text', type: 'short_text', label: 'Short text', hint: 'One line' },
  { id: 'long_text', type: 'long_text', label: 'Long text', hint: 'A paragraph' },
  { id: 'dropdown', type: 'dropdown', label: 'Dropdown', hint: 'Pick one from a list' },
  { id: 'multiple_choice', type: 'multiple_choice', label: 'Multiple choice', hint: 'Pick one, all visible' },
  {
    id: 'yes_no',
    type: 'multiple_choice',
    label: 'Yes / No',
    hint: 'Radio, two options',
    presetOptions: ['Yes', 'No'],
  },
  { id: 'checkboxes', type: 'checkboxes', label: 'Checkboxes', hint: 'Pick any' },
]

let localKeyCounter = 0

export function initQuestionsEditor(appointmentType?: AppointmentTypeDto): QuestionsEditorState {
  if (!appointmentType) return createModeState()
  const questions = (appointmentType.booking_questions ?? []).map(toEditorQuestion)
  return { questions, baseline: questions, seededMessageKey: null }
}

export function isQuestionsDirty(state: QuestionsEditorState): boolean {
  return (
    JSON.stringify(toSaveQuestionsPayload(state.questions)) !== JSON.stringify(toSaveQuestionsPayload(state.baseline))
  )
}

export function hasOwnerQuestions(state: QuestionsEditorState): boolean {
  return state.questions.some((question) => (question.origin ?? 'owner') === 'owner')
}

export function remainingQuestionRoom(state: QuestionsEditorState): number {
  return Math.max(0, MAX_BOOKING_QUESTIONS - state.questions.length)
}

export function canAddQuestion(state: QuestionsEditorState): boolean {
  return remainingQuestionRoom(state) > 0
}

export function addQuestion(state: QuestionsEditorState, draft: QuestionDraft): QuestionsEditorState {
  if (!canAddQuestion(state)) return state
  const question = questionFromDraft(draft, { key: createLocalKey('question'), type: draft.type, origin: 'owner' })
  return { ...state, questions: [...state.questions, question] }
}

export function updateQuestion(state: QuestionsEditorState, key: string, draft: QuestionDraft): QuestionsEditorState {
  const questions = state.questions.map((question) =>
    question.key === key ? questionFromDraft(draft, question) : question,
  )
  return { ...state, questions }
}

export function deleteQuestion(state: QuestionsEditorState, key: string): QuestionsEditorState {
  return { ...state, questions: state.questions.filter((question) => question.key !== key) }
}

export function moveQuestion(state: QuestionsEditorState, activeKey: string, overKey: string): QuestionsEditorState {
  const from = state.questions.findIndex((question) => question.key === activeKey)
  const to = state.questions.findIndex((question) => question.key === overKey)
  if (from < 0 || to < 0 || from === to) return state
  return { ...state, questions: moveItem(state.questions, from, to) }
}

export function copyQuestionsFrom(state: QuestionsEditorState, source: BookingQuestion[]): CopyResult {
  if (source.length === 0) return { state, message: null }
  const room = remainingQuestionRoom(state)
  if (source.length > room) return { state, message: copyOverflowMessage(room) }
  const copies = source.map((question) => withoutId(toEditorQuestion(question)))
  return { state: { ...state, questions: [...state.questions, ...copies] }, message: null }
}

export function copyOverflowMessage(room: number): string {
  if (room <= 0) return 'No more questions fit'
  if (room === 1) return 'Only 1 more question fits'
  return `Only ${room} more questions fit`
}

export function mergeSeededMessageId(
  state: QuestionsEditorState,
  serverQuestions: BookingQuestion[],
): QuestionsEditorState {
  const key = state.seededMessageKey
  const serverMessage = serverQuestions.find((question) => question.origin === 'seeded_message')
  if (!key || !serverMessage) return state
  const withServerId = (rows: EditorQuestion[]) =>
    rows.map((row) => (row.key === key ? { ...row, id: serverMessage.id } : row))
  return { questions: withServerId(state.questions), baseline: withServerId(state.baseline), seededMessageKey: null }
}

export function toSaveQuestionsPayload(questions: EditorQuestion[]): BookingQuestionInput[] {
  return questions.map((question) => ({
    ...(question.id ? { id: question.id } : {}),
    type: question.type,
    label: question.label,
    required: question.required,
    origin: question.origin ?? 'owner',
    ...(isChoiceType(question.type) ? { options: [...(question.options ?? [])] } : {}),
  }))
}

export function isChoiceType(type: BookingQuestionType): boolean {
  return (CHOICE_BOOKING_QUESTION_TYPES as readonly BookingQuestionType[]).includes(type)
}

export function createDraft(tile: QuestionTile, previous?: QuestionDraft): QuestionDraft {
  const presets = tile.presetOptions ?? (isChoiceType(tile.type) ? [''] : [])
  return {
    type: tile.type,
    label: previous?.label ?? '',
    required: previous?.required ?? false,
    options: presets.map(createDraftOption),
  }
}

export function draftFromQuestion(question: EditorQuestion): QuestionDraft {
  return {
    type: question.type,
    label: question.label,
    required: question.required,
    options: (question.options ?? []).map(createDraftOption),
  }
}

export function canAddDraftOption(draft: QuestionDraft): boolean {
  return draft.options.length < MAX_BOOKING_QUESTION_OPTIONS
}

export function insertDraftOption(draft: QuestionDraft, afterKey: string | null, value = ''): InsertOptionResult {
  if (!canAddDraftOption(draft)) return { draft, insertedKey: null }
  const option = createDraftOption(clampToCodePoints(value, MAX_BOOKING_QUESTION_OPTION_CHARS))
  const afterIndex = draft.options.findIndex((existing) => existing.key === afterKey)
  const position = afterIndex >= 0 ? afterIndex + 1 : draft.options.length
  const options = [...draft.options.slice(0, position), option, ...draft.options.slice(position)]
  return { draft: { ...draft, options }, insertedKey: option.key }
}

export function changeDraftOption(draft: QuestionDraft, key: string, value: string): QuestionDraft {
  const clamped = clampToCodePoints(value, MAX_BOOKING_QUESTION_OPTION_CHARS)
  return {
    ...draft,
    options: draft.options.map((option) => (option.key === key ? { ...option, value: clamped } : option)),
  }
}

export function removeDraftOption(draft: QuestionDraft, key: string): QuestionDraft {
  return { ...draft, options: draft.options.filter((option) => option.key !== key) }
}

export function moveDraftOption(draft: QuestionDraft, activeKey: string, overKey: string): QuestionDraft {
  const from = draft.options.findIndex((option) => option.key === activeKey)
  const to = draft.options.findIndex((option) => option.key === overKey)
  if (from < 0 || to < 0 || from === to) return draft
  return { ...draft, options: moveItem(draft.options, from, to) }
}

export function clampToCodePoints(text: string, max: number): string {
  return countCodePoints(text) > max ? Array.from(text).slice(0, max).join('') : text
}

export function validateDraft(draft: QuestionDraft): DraftErrors {
  const errors: DraftErrors = { label: labelError(draft.label), optionErrors: {} }
  if (!isChoiceType(draft.type)) return errors
  if (draft.options.length === 0) errors.options = OPTIONS_REQUIRED_ERROR
  errors.optionErrors = optionErrors(draft.options)
  return errors
}

export function hasDraftErrors(errors: DraftErrors): boolean {
  return Boolean(errors.label || errors.options || Object.keys(errors.optionErrors).length > 0)
}

/** Before the owner tries to confirm, only mistakes they just made are shown, not untouched blanks. */
export function visibleDraftErrors(errors: DraftErrors, attempted: boolean): DraftErrors {
  if (attempted) return errors
  const duplicates = Object.entries(errors.optionErrors).filter(([, message]) => message === OPTION_DUPLICATE_ERROR)
  return { options: errors.options, optionErrors: Object.fromEntries(duplicates) }
}

export function createLocalKey(prefix: string): string {
  localKeyCounter += 1
  return `${prefix}-${localKeyCounter}`
}

function createModeState(): QuestionsEditorState {
  const seeded: EditorQuestion = {
    key: createLocalKey('question'),
    type: 'long_text',
    label: SEEDED_MESSAGE_LABEL,
    required: false,
    origin: 'seeded_message',
  }
  return { questions: [seeded], baseline: [seeded], seededMessageKey: seeded.key }
}

function toEditorQuestion(question: BookingQuestion): EditorQuestion {
  return {
    key: createLocalKey('question'),
    id: question.id,
    type: question.type,
    label: question.label,
    required: question.required,
    origin: question.origin,
    ...(question.options ? { options: [...question.options] } : {}),
  }
}

function withoutId(question: EditorQuestion): EditorQuestion {
  const copy = { ...question }
  delete copy.id
  return copy
}

function questionFromDraft(
  draft: QuestionDraft,
  base: Pick<EditorQuestion, 'key' | 'id' | 'type' | 'origin'>,
): EditorQuestion {
  const question: EditorQuestion = {
    ...(base.id ? { id: base.id } : {}),
    key: base.key,
    type: base.type,
    label: sanitizeBookingText(draft.label),
    required: draft.required,
    origin: base.origin,
  }
  if (!isChoiceType(base.type)) return question
  return { ...question, options: draft.options.map((option) => sanitizeBookingText(option.value)) }
}

function createDraftOption(value: string): DraftOption {
  return { key: createLocalKey('option'), value }
}

function labelError(label: string): string | undefined {
  const sanitized = sanitizeBookingText(label)
  if (!sanitized) return LABEL_REQUIRED_ERROR
  if (countCodePoints(sanitized) > MAX_BOOKING_QUESTION_LABEL_CHARS) return LABEL_TOO_LONG_ERROR
  return undefined
}

function optionErrors(options: DraftOption[]): Record<string, string> {
  const errors: Record<string, string> = {}
  const seen = new Set<string>()
  options.forEach((option) => {
    const sanitized = sanitizeBookingText(option.value)
    const duplicateKey = bookingOptionDuplicateKey(option.value)
    if (!sanitized) errors[option.key] = OPTION_EMPTY_ERROR
    else if (countCodePoints(sanitized) > MAX_BOOKING_QUESTION_OPTION_CHARS) errors[option.key] = OPTION_TOO_LONG_ERROR
    else if (seen.has(duplicateKey)) errors[option.key] = OPTION_DUPLICATE_ERROR
    seen.add(duplicateKey)
  })
  return errors
}

function moveItem<T>(items: T[], from: number, to: number): T[] {
  const next = [...items]
  const [item] = next.splice(from, 1)
  next.splice(to, 0, item)
  return next
}
